<template>
    <div v-if="normalizedFiles.length === 1" class="brochure-card" @click="openFile(normalizedFiles[0])">
        <div class="brochure-left">
            <FileText />
        </div>

        <div class="brochure-info">
            <div class="brochure-name">
                {{ getPrettyName(normalizedFiles[0]) }}
            </div>
        </div>

        <div class="brochure-action">
            <ArrowRight />
        </div>
    </div>

    <div v-else-if="loopFiles.length" class="brochure-scroll" ref="scrollContainer">
        <div v-for="(file, index) in loopFiles" :key="index" class="brochure-item" @click="openFile(file)">
            <FileText class="icon" />

            <span class="name">
                {{ getPrettyName(file) }}
            </span>

            <ArrowRight class="arrow" />
        </div>
    </div>
</template>

<script>
import { FileText, ArrowRight } from 'lucide-vue-next'

export default {
    name: 'BrochureCard',

    components: {
        FileText,
        ArrowRight
    },

    props: {
        file: String,
        files: Array,
        baseUrl: String
    },

    data() {
        return {
            rafId: null,
            shouldLoop: false,
            isUserInteracting: false,
            isMobile: false
        }
    },

    computed: {

        normalizedFiles() {

            if (Array.isArray(this.files) && this.files.length) {
                return this.files
            }

            if (typeof this.file === 'string' && this.file.trim()) {
                return [
                    {
                        name: this.file.split('/').pop(),
                        file: this.file
                    }
                ]
            }
            if (typeof this.file === 'object' && this.file !== null) {
                return [this.file]
            }

            return []
        },

        loopFiles() {

            if (!this.shouldLoop) {
                return this.normalizedFiles
            }

            return [
                ...this.normalizedFiles,
                ...this.normalizedFiles,
                ...this.normalizedFiles,
                ...this.normalizedFiles,
                ...this.normalizedFiles
            ]
        }
    },

    mounted() {

        if (this.normalizedFiles.length <= 1) return

        const el = this.$refs.scrollContainer
        if (!el) return

        this.isMobile = window.innerWidth < 768

        this.$nextTick(() => {

            this.shouldLoop = this.normalizedFiles.length > 1

            if (this.shouldLoop) {
                this.startAutoScroll()
            }
        })

        el.addEventListener('mousedown', () => {
            this.isUserInteracting = true
        })

        el.addEventListener('mouseup', () => {
            this.isUserInteracting = false
        })

        el.addEventListener('mouseleave', () => {
            this.isUserInteracting = false
        })

        el.addEventListener('wheel', () => {

            this.isUserInteracting = true

            clearTimeout(this._scrollTimeout)

            this._scrollTimeout = setTimeout(() => {
                this.isUserInteracting = false
            }, 1200)
        })

        el.addEventListener('touchstart', () => {
            this.isUserInteracting = true
        })

        el.addEventListener('touchend', () => {

            clearTimeout(this._touchTimeout)

            this._touchTimeout = setTimeout(() => {
                this.isUserInteracting = false
            }, 1200)
        })
    },

    beforeUnmount() {
        cancelAnimationFrame(this.rafId)
    },

    methods: {

        startAutoScroll() {

            const el = this.$refs.scrollContainer
            if (!el) return

            let velocity = 0

            const speed = this.isMobile ? 0.45 : 0.20

            const originalWidth = el.scrollWidth / 5

            el.scrollLeft = originalWidth * 2

            const step = () => {

                if (!this.isUserInteracting) {

                    velocity += speed

                    if (velocity >= 1) {

                        el.scrollLeft += Math.floor(velocity)

                        velocity = velocity % 1
                    }
                }

                if (el.scrollLeft >= originalWidth * 4) {
                    el.scrollLeft = originalWidth * 2
                }

                if (el.scrollLeft <= originalWidth) {
                    el.scrollLeft = originalWidth * 2
                }

                this.rafId = requestAnimationFrame(step)
            }

            step()
        },

        getFileUrl(file) {

            const filePath = typeof file === 'object'
                ? file.file
                : file

            if (!filePath || !this.baseUrl) return ''

            return `${this.baseUrl}/${filePath.replace('assets/', '')}`
        },

        getPrettyName(file) {

            if (!file) return 'Archivo'

            if (typeof file === 'object' && file.name) {
                return file.name
            }

            const filePath = typeof file === 'object'
                ? file.file
                : file

            if (!filePath || typeof filePath !== 'string') {
                return 'Archivo'
            }

            let name = filePath.split('/').pop()

            name = name.replace(/\.[^/.]+$/, '')

            name = name.replace(/[-_]/g, ' ')

            name = name
                .toLowerCase()
                .replace(/\b\w/g, l => l.toUpperCase())

            return name
        },

        openFile(file) {

            const url = this.getFileUrl(file)

            if (!url) return

            window.open(url, '_blank')
        }
    }
}
</script>

<style scoped>
/* ===== 1 ARCHIVO (ORIGINAL) ===== */

.brochure-card {
    display: flex;
    align-items: center;
    justify-content: space-between;

    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid #e8e8e8;
    background: #ffffff;

    cursor: pointer;
    transition: all 0.18s ease;
    margin-bottom: 6px;
}

.brochure-card:hover {
    background: #f7f9fb;
    border-color: #4d9ec1;
}

.brochure-left {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f0f6f9;

    display: flex;
    align-items: center;
    justify-content: center;

    color: #4d9ec1;
}

.brochure-left svg {
    width: 16px;
    height: 16px;
}

.brochure-info {
    flex: 1;
    margin-left: 8px;
    text-align: left;
}

.brochure-name {
    font-size: 12.5px;
    font-weight: 600;
    color: #262626;
}

.brochure-action {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    transition: all 0.2s ease;
}

.brochure-action svg {
    width: 14px;
    height: 14px;
}

.brochure-card:hover .brochure-action {
    transform: translateX(3px);
    color: #4d9ec1;
}

/* ===== SCROLL ===== */

.brochure-scroll {
    display: flex;
    gap: 8px;

    overflow-x: auto;
    padding-bottom: 8px;
    margin-bottom: 12px;

    scrollbar-width: none;
}

.brochure-scroll::-webkit-scrollbar {
    display: none;
}

.brochure-item {
    flex: 0 0 auto;

    display: flex;
    align-items: center;
    gap: 6px;

    padding: 6px 10px;
    border-radius: 8px;

    background: #f7f9fb;
    border: 1px solid #e6eef2;

    font-size: 12px;
    font-weight: 500;
    color: #333;

    white-space: nowrap;

    cursor: pointer;
    transition: all 0.18s ease;
}

.brochure-item:hover {
    background: #eef6fa;
    border-color: #36e0f7;
}

.icon {
    width: 14px;
    height: 14px;
    color: #36e0f7;
}

.name {
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
}

.arrow {
    width: 14px;
    height: 14px;
    color: #aaa;
    transition: transform 0.2s ease;
}

.brochure-item:hover .arrow {
    transform: translateX(3px);
    color: #36e0f7;
}
</style>